/**
 * Specialized notification formatters for specific use cases
 */

import { calculateNetPayableAmount, calculateNetPayableKg, getQualityCompensationKg, getWarehouseReceivedKg } from '@/utils/procurementCalculations';
import { getProductsSummary, formatRequestorInfo, formatQualityInfo } from './utils';

/**
 * Format a notification for upcoming payment (text-only, no buttons)
 */
export const formatPaymentReminderNotification = (procurement: any, daysRemaining: number): string => {
  const requestorInfo = procurement.requestor 
    ? `\nRequested by: ${procurement.requestor.first_name || ''} ${procurement.requestor.last_name || ''}${procurement.requestor.email ? ` (${procurement.requestor.email})` : ''}`
    : '\nRequestor: Unknown';

  // Use the payment term type from finance data
  const paymentTermType = procurement.finance?.payment_term_type || procurement.payment_term_type;
  const paymentDueDate = procurement.finance?.payment_due_date || procurement.payment_due_date;

  // Handle upfront payments specifically
  let dueDateInfo: string;
  if (paymentTermType === 'upfront') {
    if (daysRemaining <= 0) {
      dueDateInfo = 'Due: Immediately (upfront payment - overdue)';
    } else {
      dueDateInfo = 'Due: Immediately (upfront payment)';
    }
  } else {
    dueDateInfo = `Due in: ${daysRemaining} days\nDue date: ${paymentDueDate ? new Date(paymentDueDate).toLocaleDateString() : 'Not set'}`;
  }

  const productSummary = getProductsSummary(procurement);
  
  // Use the accurate item-level calculations
  const receivedQty = getWarehouseReceivedKg(procurement);
  const compensationKg = getQualityCompensationKg(procurement);
  const netPayableKg = calculateNetPayableKg(procurement);
  const netPayableAmount = calculateNetPayableAmount(procurement);
  
  // Quality info for compensation - now with preliminary vs final support
  const quality = procurement.quality;
  const isPreliminary = quality?.grading_type === 'preliminary';
  const isFinalComplete = quality?.is_final_grading_complete;

  let qualitySection = '';
  if (compensationKg > 0 || isPreliminary) {
    // Add grading type indicator
    if (isPreliminary) {
      qualitySection += `\n📋 **Grading Type:** ${isFinalComplete ? 'Final Complete' : 'Preliminary (Sample-based)'}`;
      
      if (!isFinalComplete && quality?.final_grading_expected_date) {
        qualitySection += `\n⏰ Expected Final Grading: ${new Date(quality.final_grading_expected_date).toLocaleDateString()}`;
      }
      
      // Show preliminary values
      const prelimComp = quality.preliminary_compensation_kg || 0;
      const finalComp = quality.compensation_kg || 0;
      
      qualitySection += `\n\n⚠️ **${isFinalComplete ? 'Preliminary' : 'Estimated'} Values:**`;
      qualitySection += `\nEstimated Damage: ${quality.preliminary_damage_kg || 0} kg`;
      qualitySection += `\nEstimated Compensation: ${prelimComp} kg`;
      
      // Show final values if complete
      if (isFinalComplete) {
        qualitySection += `\n\n✅ **Final Values:**`;
        qualitySection += `\nActual Damage: ${quality.damage_kg || 0} kg`;
        qualitySection += `\nActual Compensation: ${finalComp} kg`;
        
        // Show difference
        const diff = finalComp - prelimComp;
        if (diff !== 0) {
          qualitySection += `\n\n🔄 **Reconciliation:**`;
          qualitySection += `\nDifference: ${diff > 0 ? '+' : ''}${diff} kg`;
          qualitySection += diff > 0 
            ? `\n⚠️ Final damage was MORE than estimated`
            : `\n✅ Final damage was LESS than estimated`;
        }
      }
    } else if (compensationKg > 0) {
      // Non-preliminary quality findings
      qualitySection = `\n⚠️ **Quality Findings:**`;
      qualitySection += `\nAgreed Compensation: ${compensationKg.toFixed(2)} kg`;
    }
    
    if (compensationKg > 0) {
      qualitySection += `\n\n💰 **Net Payable Qty:** ${netPayableKg.toFixed(2)} kg`;
    }
  }

  // Remove bank info from payment reminders - no longer needed

  return `💰 *Payment Reminder*\n\n` +
    `ID: ${procurement.id.substring(0, 8)}\n` +
    `Product: ${productSummary}\n` +
    `Received Quantity: ${receivedQty.toFixed(2)} kg\n` +
    `${compensationKg > 0 ? `Net Payable Amount: Br${netPayableAmount.toFixed(2)}` : `Amount Due: Br${netPayableAmount.toFixed(2)}`}\n` +
    dueDateInfo +
    qualitySection +
    requestorInfo + `\n\n` +
    `To view procurement details, use command:\n` +
    `/status ${procurement.id.substring(0, 8)}`;
};

/**
 * Format a notification for a delivery update
 */
export const formatDeliveryNotification = (procurement: any): string => {
  const productSummary = getProductsSummary(procurement);
  const totalQuantity = procurement.total_quantity || 0;
  const qualityInfo = formatQualityInfo(procurement);
  
  return `🚚 *Delivery Update*\n\n` +
    `ID: ${procurement.id.substring(0, 8)}\n` +
    `Product: ${productSummary}\n` +
    `Quantity: ${totalQuantity} kg\n` +
    `Expected Date: ${procurement.expected_delivery_date ? new Date(procurement.expected_delivery_date).toLocaleDateString() : 'Not set'}` +
    `${qualityInfo}\n\n` +
    `Please prepare for the incoming delivery.\n` +
    `To view details, use command:\n` +
    `/status ${procurement.id.substring(0, 8)}`;
};

/**
 * Format a notification for quality verification
 */
export const formatQualityVerificationNotification = (procurement: any): string => {
  const productSummary = getProductsSummary(procurement);
  const totalQuantity = procurement.total_quantity || 0;
  const qualityInfo = formatQualityInfo(procurement);
  
  return `✅ *Quality Verification Required*\n\n` +
    `ID: ${procurement.id.substring(0, 8)}\n` +
    `Product: ${productSummary}\n` +
    `Quantity: ${totalQuantity} kg\n` +
    `Grade: ${procurement.quality_grade || 'Not specified'}` +
    `${qualityInfo}\n\n` +
    `Please verify the quality as soon as possible.\n` +
    `To view details, use command:\n` +
    `/status ${procurement.id.substring(0, 8)}`;
};

/**
 * Format a notification for final grading completion
 * Focus on quality reconciliation only - no net payable amounts
 */
export const formatFinalGradingNotification = (procurement: any): string => {
  const productSummary = getProductsSummary(procurement);
  const totalQuantity = procurement.total_quantity || 0;
  const quality = procurement.quality;
  
  const prelimComp = quality?.preliminary_compensation_kg || 0;
  const finalComp = quality?.compensation_kg || 0;
  const diff = finalComp - prelimComp;
  
  let reconciliationInfo = '';
  if (diff !== 0) {
    reconciliationInfo = `\n\n🔄 **Reconciliation Note:**\n` +
      `Preliminary Compensation: ${prelimComp} kg\n` +
      `Final Compensation: ${finalComp} kg\n` +
      `Difference: ${diff > 0 ? '+' : ''}${diff} kg`;
  }
  
  return `✅ *Final Grading Complete*\n\n` +
    `ID: ${procurement.id.substring(0, 8)}\n` +
    `Product: ${productSummary}\n` +
    `Total Quantity: ${totalQuantity} kg\n` +
    `\n**Final Assessment:**\n` +
    `Actual Damage: ${quality?.damage_kg || 0} kg\n` +
    `Actual Compensation: ${finalComp} kg` +
    reconciliationInfo +
    `\n\nTo view details, use command:\n` +
    `/status ${procurement.id.substring(0, 8)}`;
};
